/**
 * CreatePostModal - Twitter-style centered modal for creating posts
 * Modern, minimalistic design with overlay
 */

import { useState, useRef, useEffect } from 'react'
import { X, Images, Loader2, Cloud, Users } from 'lucide-react'
import api from '../services/api'
import Avatar from './ui/Avatar'

interface CreatePostModalProps {
  isOpen: boolean
  onClose: () => void
}

export default function CreatePostModal({ isOpen, onClose }: CreatePostModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const [text, setText] = useState('')
  const [mediaFile, setMediaFile] = useState<File | null>(null)
  const [mediaPreview, setMediaPreview] = useState<string | null>(null)
  const [mediaType, setMediaType] = useState<'image' | 'video' | null>(null)
  const [isPosting, setIsPosting] = useState(false)

  // Autocomplete state
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [suggestionType, setSuggestionType] = useState<'mention' | 'hashtag' | null>(null)
  const [cursorPosition, setCursorPosition] = useState(0)

  // Get current user from localStorage
  const userProfile = JSON.parse(localStorage.getItem('user_profile') || '{}')
  const user = {
    name: (userProfile.name && userProfile.name !== 'User' && userProfile.name !== 'Your Name' && userProfile.name !== '')
      ? userProfile.name
      : userProfile.username || 'User',
    username: userProfile.username || 'user',
    avatar: userProfile.avatar,
  }

  const charLimit = 400
  const charsRemaining = charLimit - text.length

  // Mock data for autocomplete (replace with API calls later)
  const mockUsers = ['artist.ravi', 'john_doe', 'jane_smith', 'onrecord_official']
  const mockHashtags = ['breaking', 'news', 'onrecord', 'trending', 'journalism']

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPosting) {
        handleClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, isPosting])

  const handleClose = () => {
    if (isPosting) return

    // If there's content, confirm before closing
    if (text.trim() || mediaFile) {
      if (window.confirm('Discard post? Your changes will be lost.')) {
        resetForm()
        onClose()
      }
    } else {
      resetForm()
      onClose()
    }
  }

  const resetForm = () => {
    setText('')
    setMediaFile(null)
    setMediaPreview(null)
    setMediaType(null)
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value
    const cursorPos = e.target.selectionStart

    if (newText.length <= charLimit) {
      setText(newText)
      setCursorPosition(cursorPos)

      // Check for @ or # trigger for autocomplete
      const textBeforeCursor = newText.substring(0, cursorPos)
      const lastWord = textBeforeCursor.split(/\s/).pop() || ''

      if (lastWord.startsWith('@') && lastWord.length > 1) {
        // Mention autocomplete
        const query = lastWord.substring(1).toLowerCase()
        const filtered = mockUsers.filter(user =>
          user.toLowerCase().includes(query)
        )
        setSuggestions(filtered)
        setSuggestionType('mention')
        setShowSuggestions(filtered.length > 0)
      } else if (lastWord.startsWith('#') && lastWord.length > 1) {
        // Hashtag autocomplete
        const query = lastWord.substring(1).toLowerCase()
        const filtered = mockHashtags.filter(tag =>
          tag.toLowerCase().includes(query)
        )
        setSuggestions(filtered)
        setSuggestionType('hashtag')
        setShowSuggestions(filtered.length > 0)
      } else {
        setShowSuggestions(false)
      }
    }
  }

  const handleSuggestionClick = (suggestion: string) => {
    const textBeforeCursor = text.substring(0, cursorPosition)
    const textAfterCursor = text.substring(cursorPosition)
    const words = textBeforeCursor.split(/\s/)
    words[words.length - 1] = (suggestionType === 'mention' ? '@' : '#') + suggestion + ' '
    const newText = words.join(' ') + textAfterCursor

    setText(newText)
    setShowSuggestions(false)

    // Focus back on textarea
    setTimeout(() => {
      textareaRef.current?.focus()
      const newCursorPos = words.join(' ').length
      textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos)
    }, 0)
  }

  const handleMediaSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      alert('File size must be less than 50MB')
      return
    }

    // Validate file type
    const isImage = file.type.startsWith('image/')
    const isVideo = file.type.startsWith('video/')

    if (!isImage && !isVideo) {
      alert('Please select an image or video file')
      return
    }

    setMediaFile(file)
    setMediaType(isImage ? 'image' : 'video')

    // Create preview
    const reader = new FileReader()
    reader.onload = (event) => {
      if (event.target?.result) {
        setMediaPreview(event.target.result as string)
      }
    }
    reader.readAsDataURL(file)
  }

  const handleRemoveMedia = () => {
    setMediaFile(null)
    setMediaPreview(null)
    setMediaType(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handlePost = async () => {
    // Validate
    if (!text.trim() && !mediaFile) {
      alert('Please add some text or media to your post')
      return
    }

    try {
      setIsPosting(true)

      const result = await api.createPost({
        text: text.trim(),
        mediaFile: mediaFile || undefined,
        mediaType: mediaType || undefined,
      })

      if (result.success) {
        // Success! Close modal and reset
        resetForm()
        onClose()

        // Refresh feed if on home page
        if (window.location.pathname === '/') {
          window.location.reload()
        }
      } else {
        alert(result.message || 'Failed to create post')
      }
    } catch (error) {
      console.error('Create post error:', error)
      alert('Failed to create post')
    } finally {
      setIsPosting(false)
    }
  }

  const canPost = (text.trim() || mediaFile) && !isPosting

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-12 px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal Card */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            disabled={isPosting}
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>

          <button
            onClick={handlePost}
            disabled={!canPost}
            className={`
              px-6 py-2 rounded-full font-bold transition-all
              ${canPost
                ? 'bg-onrecord-orange text-white hover:bg-orange-600'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }
            `}
          >
            {isPosting ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Posting...</span>
              </div>
            ) : (
              'Post'
            )}
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* User Info */}
          <div className="flex gap-3 mb-3">
            <Avatar
              src={user.avatar}
              alt={user.name}
              size="lg"
            />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-900 truncate">{user.name}</div>
              <div className="text-sm text-gray-500 truncate">@{user.username}</div>
            </div>
          </div>

          {/* Text Input with Character Counter */}
          <div className="mb-4 relative">
            <textarea
              ref={textareaRef}
              value={text}
              onChange={handleTextChange}
              placeholder="go onrecord"
              className="w-full text-lg resize-none outline-none placeholder-gray-400"
              rows={4}
              disabled={isPosting}
              autoFocus
            />

            {/* Autocomplete Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-50 w-64">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors flex items-center gap-2"
                  >
                    {suggestionType === 'mention' ? (
                      <>
                        <div className="w-8 h-8 bg-gray-300 rounded-full flex-shrink-0" />
                        <div>
                          <div className="font-medium text-sm">@{suggestion}</div>
                          <div className="text-xs text-gray-500">User</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="text-onrecord-orange font-bold">#</span>
                        <span className="font-medium text-sm">{suggestion}</span>
                      </>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Character Counter - Always Visible */}
            <div className="flex justify-end mt-2">
              <div
                className={`text-sm font-medium ${
                  charsRemaining < 20
                    ? 'text-red-500'
                    : charsRemaining < 50
                    ? 'text-orange-500'
                    : 'text-gray-400'
                }`}
              >
                {text.length}/{charLimit}
              </div>
            </div>
          </div>

          {/* Media Preview */}
          {mediaPreview && (
            <div className="relative mb-4 rounded-xl overflow-hidden border border-gray-200">
              {mediaType === 'image' ? (
                <img
                  src={mediaPreview}
                  alt="Upload preview"
                  className="w-full max-h-80 object-contain bg-gray-50"
                />
              ) : (
                <video
                  src={mediaPreview}
                  controls
                  className="w-full max-h-80 object-contain bg-gray-50"
                />
              )}

              {/* Remove Media Button */}
              <button
                onClick={handleRemoveMedia}
                className="absolute top-2 right-2 p-2 bg-black/60 hover:bg-black/80 rounded-full transition-colors"
                disabled={isPosting}
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          )}

          {/* Media Actions */}
          <div className="border-t border-gray-200 pt-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                {/* Gallery Media - Local Device */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 hover:bg-orange-50 rounded-full transition-colors group"
                  disabled={isPosting || !!mediaPreview}
                  title="Gallery media"
                >
                  <Images className="w-5 h-5 text-onrecord-orange" />
                </button>

                {/* Hidden File Input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleMediaSelect}
                  className="hidden"
                />

                {/* Cloud Media - Private on record Server */}
                <button
                  className="p-2 hover:bg-orange-50 rounded-full transition-colors group"
                  disabled={isPosting || !!mediaPreview}
                  title="Private media"
                >
                  <Cloud className="w-5 h-5 text-onrecord-orange" />
                </button>

                {/* Shared Media */}
                <button
                  className="p-2 hover:bg-orange-50 rounded-full transition-colors group"
                  disabled={isPosting || !!mediaPreview}
                  title="Shared media"
                >
                  <Users className="w-5 h-5 text-onrecord-orange" />
                </button>
              </div>

              {/* Character count indicator (circular) */}
              {text.length > 0 && (
                <div className="flex items-center gap-2">
                  <svg className="w-6 h-6 -rotate-90" viewBox="0 0 24 24">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="2"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      fill="none"
                      stroke={
                        charsRemaining < 20
                          ? '#ef4444'
                          : charsRemaining < 50
                          ? '#f97316'
                          : '#ff6b35'
                      }
                      strokeWidth="2"
                      strokeDasharray={`${(text.length / charLimit) * 62.83} 62.83`}
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
